import { formatSurfaceDate } from '../../utils/dateDisplay';
import { docText, normalizeSpaces, sentenceBoundaryTrim } from '../../utils/editorialText';

const EMPTY_DOC = { type: 'doc', content: [] };

export const countWikiWords = (value = '') => String(value || '').split(/\s+/).filter(Boolean).length;

export const countWikiClaimMarks = (node, out = new Set()) => {
  if (!node) return out;
  if (Array.isArray(node)) {
    node.forEach(child => countWikiClaimMarks(child, out));
    return out;
  }
  if (typeof node !== 'object') return out;
  (node.marks || []).forEach((mark) => {
    if (mark?.type !== 'claim') return;
    const attrs = mark.attrs || {};
    out.add(attrs.claimId ? String(attrs.claimId) : `${docText(node).slice(0, 120)}:${out.size}`);
  });
  if (Array.isArray(node.content)) countWikiClaimMarks(node.content, out);
  return out;
};

export const countWikiSources = (page = {}) => {
  page = page || {};
  const explicit = Number(page.sourceCount ?? page.sourcesCount);
  const sourceIds = new Set();
  [...(Array.isArray(page.sourceRefs) ? page.sourceRefs : []), ...(Array.isArray(page.sources) ? page.sources : [])]
    .forEach((source, index) => {
      sourceIds.add(source?._id || source?.id || source?.sourceRefId || `source-${index}`);
    });
  (Array.isArray(page.citations) ? page.citations : []).forEach((citation) => {
    const id = citation.sourceRefId || citation.sourceId || citation.sourceRef?._id || citation.sourceRef?.id;
    if (id) sourceIds.add(id);
  });
  return Math.max(Number.isFinite(explicit) ? explicit : 0, sourceIds.size);
};

export const countWikiClaims = (page = {}) => {
  page = page || {};
  const explicit = Number(page.claimCount ?? page.claimsCount);
  const claimIds = new Set();
  const retiredClaimIds = new Set();
  (Array.isArray(page.claims) ? page.claims : []).forEach((claim, index) => {
    const claimId = claim?.claimId || claim?._id || claim?.id || `claim-${index}`;
    if (claim?.checkInStatus === 'retired' || claim?.retiredAt) retiredClaimIds.add(String(claimId));
    else claimIds.add(claimId);
  });
  (Array.isArray(page.citations) ? page.citations : []).forEach((citation) => {
    const id = citation.claimId || citation.claim?._id || citation.claim?.id;
    if (id && !retiredClaimIds.has(String(id))) claimIds.add(id);
  });
  countWikiClaimMarks(page.body).forEach(id => {
    if (!retiredClaimIds.has(String(id))) claimIds.add(id);
  });
  if (Array.isArray(page.claims)) return claimIds.size;
  return Math.max(Number.isFinite(explicit) ? explicit : 0, claimIds.size);
};

export const countWikiPageWords = (page = {}, body = null) => {
  page = page || {};
  const explicit = Number(page.wordCount ?? page.wordsCount);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const bodyWords = countWikiWords(docText(body || page.body || EMPTY_DOC));
  if (bodyWords > 0) return bodyWords;
  return countWikiWords(page.plainText || page.summary || page.scope || page.description || '');
};

/* A heading is a line with no sentence ending. In a preview it would run into
   the prose after it ("Moats A moat is…"), so a preview keeps only the lines
   that read as sentences, when there are any. */
const proseLines = (value = '') => {
  const lines = String(value || '').split(/\n+/).map(line => line.trim()).filter(Boolean);
  const prose = lines.filter(line => /[.!?…:;"'”’)\]]$/.test(line));
  return (prose.length ? prose : lines).join(' ');
};

export const cleanWikiPreviewText = (value = '', title = '') => {
  let text = proseLines(value)
    .replace(/\[\s*\d+(?:\s*[,–-]\s*\d+)*\s*\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const trimmedTitle = normalizeSpaces(title);
  if (trimmedTitle && text.toLowerCase().startsWith(trimmedTitle.toLowerCase())) {
    text = text.slice(trimmedTitle.length).replace(/^[\s:–-]+/, '').trim();
  }
  return text;
};

export const clampWikiPreview = (value = '', budget = 160) => {
  return sentenceBoundaryTrim(value, { maxLength: budget, fallback: '' });
};

export const wikiPreviewForPage = (page = {}, budget = 160) => {
  page = page || {};
  const source = page.summary || page.scope || page.description || page.plainText || docText(page.body || EMPTY_DOC) || '';
  return clampWikiPreview(cleanWikiPreviewText(source, page.title), budget);
};

export const WIKI_SCAFFOLD_PATTERN = /(still needs source-backed development|needs stronger source material|no matching library evidence|not enough source material|should explain the concept|needs source material|placeholder|scaffold)/i;

export const isWikiScaffoldPage = (page = {}) => {
  page = page || {};
  const bodyText = [
    page.summary,
    page.scope,
    page.description,
    page.plainText,
    docText(page.body || EMPTY_DOC),
    ...(Array.isArray(page.aiState?.health?.issues) ? page.aiState.health.issues.map(issue => issue?.text || issue?.label || '') : [])
  ].filter(Boolean).join(' ');
  return WIKI_SCAFFOLD_PATTERN.test(bodyText);
};

export const wikiSourceStatusForPage = (page = {}) => {
  const sources = countWikiSources(page);
  if (sources > 0) return `${sources} source${sources === 1 ? '' : 's'} · ${countWikiClaims(page)} claim${countWikiClaims(page) === 1 ? '' : 's'}`;
  return isWikiScaffoldPage(page) ? 'Draft scaffold · needs sources' : 'Draft · needs sources';
};

export const formatWikiRowDate = (value) => {
  return formatSurfaceDate(value, { includeYear: true });
};

export const wikiRowMetaForPage = (page = {}) => {
  page = page || {};
  const sources = countWikiSources(page);
  if (sources === 0) return wikiSourceStatusForPage(page);
  const claims = countWikiClaims(page);
  const reviewedAt = page.lastReviewedAt || page.qualityReview?.reviewedAt;
  const parts = [
    `${sources} source${sources === 1 ? '' : 's'}`,
    `${claims} claim${claims === 1 ? '' : 's'}`
  ];
  if (reviewedAt) parts.push(`reviewed ${formatSurfaceDate(reviewedAt)}`);
  return parts.join(' · ');
};

export const isWikiSourceBackedPage = (page = {}) => countWikiSources(page) > 0;
